import type { TaskActionItem } from "@/services/api/agentic-v2/types";

export const SALTUS_ONBOARDING_KIT_TAG = "saltus_onboarding";

export const SALTUS_ONBOARDING_REVIEW_NODES = new Set([
  "capture_lead",
  "dedupe_review",
  "complete_mandatory",
  "crm_handoff_stub",
  "crm_handoff_stub_after_revalidate",
  "crm_handoff_reactivate_stub",
  "crm_handoff_lost_stub",
]);

export const PLACEHOLDER_CLIENT_NAMES = new Set([
  "none none",
  "unnamed client",
  "unnamed lead",
  "new lead",
  "client",
  "lead",
]);

export function isPlaceholderClientName(name: string | null | undefined): boolean {
  const trimmed = name?.trim();
  if (!trimmed) return true;
  return PLACEHOLDER_CLIENT_NAMES.has(trimmed.toLowerCase());
}

type SaltusOnboardingTaskLike = {
  spawned_from_node?: string | null;
  mission_template_tags?: string[] | null;
  tags?: string[] | null;
};

export function isSaltusOnboardingReviewTask(task: SaltusOnboardingTaskLike): boolean {
  const node = task.spawned_from_node?.trim();
  if (node && SALTUS_ONBOARDING_REVIEW_NODES.has(node)) return true;
  const tags = task.mission_template_tags ?? task.tags ?? [];
  return tags.includes(SALTUS_ONBOARDING_KIT_TAG);
}

type TaskReviewFocusTask = SaltusOnboardingTaskLike & {
  review_artefacts?: Array<{ kind?: string | null }> | null;
};

/** Saltus onboarding opens in case/context mode; letter-first tasks keep letter focus. */
export function taskReviewInitialFocus(task: TaskReviewFocusTask): "case" | "letter" {
  if (isSaltusOnboardingReviewTask(task)) return "case";
  const hasLetterArtefact = (task.review_artefacts ?? []).some((artefact) => {
    const kind = artefact.kind?.trim().toLowerCase();
    return kind === "suitability_letter" || kind === "letter";
  });
  return hasLetterArtefact ? "letter" : "case";
}

/** Gaps hint for the complete panel header. */
export function factfindGapsHint(gaps: {
  missing: string[];
  pending_review: string[];
  sections: { section: string; count: number }[];
}): string {
  if (gaps.missing.length === 0) return "Every mandatory field is in. Submit when ready.";
  const sections = gaps.sections
    .filter((row) => row.count > 0)
    .map((row) => `${row.section} (${row.count})`)
    .join(", ");
  return `${gaps.missing.length} missing${sections ? `: ${sections}` : ""}.`;
}

export const FACTFIND_SOURCE_LABELS: Record<string, string> = {
  client_portal: "Client portal",
  meeting_intelligence: "Meeting Intelligence",
  adviser: "Adviser",
};

/** Human-readable Factfind field label: snake_case key → Title Case. */
export function factfindFieldLabel(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function factfindSourceLabel(source: string | null | undefined): string {
  if (!source) return "Not answered";
  return FACTFIND_SOURCE_LABELS[source] ?? source;
}

function clientNameFromReviewTitle(title: string): string | null {
  const reviewPossessive = title.match(/^Review\s+(.+?)'s\b/i);
  if (reviewPossessive?.[1]?.trim()) return reviewPossessive[1].trim();
  const possessive = title.match(/\bfor\s+(.+?)'s\b/i);
  if (possessive?.[1]?.trim()) return possessive[1].trim();
  const trailing = title.match(/\bfor\s+(.+)$/i);
  return trailing?.[1]?.trim() || null;
}

/** Prefer hydrated entity names; fall back to presentation title before generic placeholders. */
export function resolveReviewClientName(task: TaskActionItem): string {
  const fromEntity = task.entity?.display_name?.trim();
  if (fromEntity && !isPlaceholderClientName(fromEntity)) {
    return fromEntity;
  }

  const fromTitle = clientNameFromReviewTitle(
    task.presentation?.title?.trim() || task.name?.trim() || "",
  );
  if (fromTitle && !isPlaceholderClientName(fromTitle)) {
    return fromTitle;
  }

  return "Lead";
}
